/**
 * Bounded backoff poll for "Pusher said it's coming, but did it actually
 * arrive?" fallback patterns. The handler is invoked once per delay tick;
 * timers carry no state — the caller decides when to `cancel()` based on
 * the response (e.g. when expected count is reached or a Complete event
 * fires).
 *
 * Idempotent: calling `schedule()` while a series is already in flight is
 * a no-op. That preserves the backoff curve instead of restarting at the
 * first delay on every tick.
 */
export interface BackoffPoller {
  /** Kick off the backoff series. No-op if a series is already running. */
  schedule: (refetch: () => Promise<unknown> | void) => void
  /** Drop every pending tick. Safe to call when nothing is scheduled. */
  cancel: () => void
}

export function createBackoffPoller(delays: number[]): BackoffPoller {
  let timeouts: ReturnType<typeof setTimeout>[] = []

  return {
    cancel() {
      for (const id of timeouts) clearTimeout(id)
      timeouts = []
    },
    schedule(refetch) {
      if (timeouts.length > 0) return
      for (const delay of delays) {
        const timer = setTimeout(() => {
          timeouts = timeouts.filter(id => id !== timer)
          void refetch()
        }, delay)
        timeouts.push(timer)
      }
    },
  }
}
